import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { formatDashSeparator } from 'LuxKit/intl/separators'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import PlaceImageLoader from 'tripPlanner/components/Chat/PlaceImageLoader'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { isExperienceSavedData, isHotelSavedData, isSppSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import TextButton from 'LuxKit/Button/TextButton'
import DayItineraryCanvas from './DayItineraryCanvas'
import ChatItemDropdown from './ChatItemDropdown'
import noop from 'lib/function/noop'
import ModalContext from 'contexts/ModalContext'
import ConfirmDeleteStopModal from './ConfirmDeleteStopModal'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import Counter, { type CounterChangeEventHandler } from 'LuxKit/Counter'
import ItineraryLoadingState from './ItineraryLoadingState'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import {
  type HotelMapViewData,
  type ExperienceMapViewData,
  type TripItineraryData,
  type ManualItineraryDeleteRequest,
  type TripItineraryItem,
  type TripItemWithoutDay,
} from 'tripPlanner/api/types'
import PlaceInfoDrawer from './PlaceInfoDrawer'
import { HotelListingDrawer } from './HotelListingDrawer'
import type { DrawerHotel } from './HotelListingDrawer'
import { ExperienceListingDrawer } from './ExperienceListingDrawer'
import ChatOfferDetailDrawer from './ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from './ChatExperienceDetailDrawer'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { buildStopDeletedActivityEntry } from '../utils/buildStopDeletedActivityEntry'
import { buildItemDeletedActivityEntry } from '../utils/buildItemDeletedActivityEntry'
import type { DrawerExperience } from './ExperienceListingDrawer/ExperienceDetailView'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import { useSortedHighlights } from '../hooks/useSortedHighlights'
import type { Highlight } from './tripPlanCanvasTypes'
import type { MapCoordinate } from 'tripPlanner/utils/mapMarkerTransforms'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'
import type { LEExploreCategorySource } from 'tripPlanner/pages/chat/chatExploreCategories'
import TripPlanHotelTile from './TripPlanHotelTile'
import useTripPlanHotelTileData from '../hooks/useTripPlanHotelTileData'
import useTripPlanExperienceTileData from '../hooks/useTripPlanExperienceTileData'
import { shouldShowTimelineStopProductTiles } from './tripPlanCanvasContent'
import StopTimingEditControl from './StopTimingEditControl'
import StopDestinationRefinement from './StopDestinationRefinement'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import {
  StopSheet,
  StopSheetContent,
  StopItinerarySheet,
  ImageContainer,
  StopMainContent,
  StopHeaderChevronButton,
  StopHeaderChevron,
  StopLearnMoreButton,
  StopDetailsPanel,
  StopDetailsContent,
  StopTitleContent,
  ClickableHeading,
  ClickableHighlightTitle,
} from './TripPlanCanvasItem.styles'
import TripPlanExperienceTile from './TripPlanExperienceTile'
import { getStopTimingSubtitle } from './stopTimingUtils'

interface Props {
  data?: TripItineraryData
  stopId: number
  placeName: string
  placeCountry: string
  stopDurationDayCount: number
  imageId?: string
  stopSeq: number
  highlights?: Array<Highlight>
  googlePlaceId?: string
  onLearnMoreClick?: (data: { googlePlaceId: string; placeName: string; countryName: string; stopId?: number }) => void
  onSendMessage?: (message: string) => Promise<void> | void
  onDeleteTarget?: (target: ManualItineraryDeleteRequest, displayName: string) => Promise<boolean> | void
  onExploreMap?: (coordinates: MapCoordinate, source: LEExploreCategorySource) => void
  onShowMap?: (data: HotelMapViewData) => void
  onShowExperienceOnMap?: (data: ExperienceMapViewData) => void
  stopCoordinates?: MapCoordinate
  isEnriching?: boolean
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
  onAskAIForItem?: (item: TripItineraryItem | TripItemWithoutDay) => void
  scrollTargetDayId?: number
  scrollTargetItemId?: number
  drawerCloseSignal?: number
  onDrawerOpenChange?: (isOpen: boolean) => void
  stopTimingEditVariant?: 'default' | 'mobile'
}

function TripPlanCanvasItem({
  data,
  stopId,
  placeName,
  placeCountry,
  stopDurationDayCount,
  imageId,
  highlights,
  googlePlaceId,
  onLearnMoreClick = noop,
  onSendMessage = noop,
  onDeleteTarget,
  onExploreMap,
  onShowMap,
  onShowExperienceOnMap,
  stopCoordinates,
  isEnriching = false,
  onAskAI,
  onAskAIForItem,
  scrollTargetDayId,
  scrollTargetItemId,
  drawerCloseSignal,
  onDrawerOpenChange,
  stopTimingEditVariant = 'default',
}: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBusOptional()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending, isManualItineraryMovePending, isFlightCartMutationPending } = useItineraryContext()
  const actionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending || isManualItineraryMovePending || isFlightCartMutationPending
  const [isStopExpanded, setIsStopExpanded] = useState(true)
  const [isCreateItineraryOpen, setIsCreateItineraryOpen] = useState(false)
  const [itineraryDays, setItineraryDays] = useState<number>(1)
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false)
  const [requestedDaysCount, setRequestedDaysCount] = useState<number>(0)
  const [isHotelDrawerOpen, setIsHotelDrawerOpen] = useState<boolean>(false)
  const [isExperienceDrawerOpen, setIsExperienceDrawerOpen] = useState<boolean>(false)
  const [selectedHighlight, setSelectedHighlight] = useState<{ googlePlaceId: string; name: string; itemId: number } | undefined>(undefined)
  const [selectedHotelHighlight, setSelectedHotelHighlight] = useState<{
    offerId: string
    accommodationPropertyId?: string
    offerType?: string
    searchParams?: OfferClickSearchParams
  } | undefined>(undefined)
  const createItineraryTriggerRef = useRef<HTMLButtonElement>(null)
  const previousDrawerCloseSignalRef = useRef(drawerCloseSignal)

  const showModal = useContext(ModalContext)

  const currentStop = useMemo(() => {
    return data?.stops.find(stop => stop.id === stopId)
  }, [data, stopId])

  const {
    bookedHotelSummary,
    hasSavedHotels,
    hotelTilePrice,
    savedHotelCount,
  } = useTripPlanHotelTileData({
    currentStop,
  })

  const {
    bookedExperienceSummary,
    bookedExperienceCount,
    hasSavedExperiences,
    experienceTilePrice,
    savedExperienceCount,
  } = useTripPlanExperienceTileData({
    currentStop,
  })

  const actualDaysCount = currentStop?.days?.length ?? 0
  const hasDays = actualDaysCount > 0
  const hasItemsWithoutDay = (currentStop?.itemsWithoutDay?.length ?? 0) > 0
  const hasHighlights = (highlights?.length ?? 0) > 0
  const showProductTiles = shouldShowTimelineStopProductTiles(currentStop)

  useEffect(() => {
    if (isGeneratingItinerary && hasDays) {
      setIsGeneratingItinerary(false)
    }
  }, [isGeneratingItinerary, hasDays])

  useEffect(() => {
    if (isGeneratingItinerary && !isStreaming && !hasDays) {
      setIsGeneratingItinerary(false)
    }
  }, [isGeneratingItinerary, isStreaming, hasDays])

  useEffect(() => {
    if (requestedDaysCount > 0 && hasDays && !isGeneratingItinerary) {
      setRequestedDaysCount(0)
    }
  }, [hasDays, isGeneratingItinerary, requestedDaysCount])

  const toggleStopExpanded = useCallback(() => {
    setIsStopExpanded(prev => !prev)
  }, [])

  const onLearnMoreClickHandler = useCallback(() => {
    if (googlePlaceId) {
      onLearnMoreClick({ googlePlaceId, placeName, countryName: placeCountry, stopId })
    }
  }, [onLearnMoreClick, googlePlaceId, placeName, placeCountry, stopId])

  const toggleCreateItineraryDropdown = useCallback(() => {
    setIsCreateItineraryOpen(prev => !prev)
  }, [])

  const closeCreateItineraryDropdown = useCallback(() => {
    setIsCreateItineraryOpen(false)
  }, [])

  const onItineraryDaysChange = useCallback<CounterChangeEventHandler>((value) => {
    setItineraryDays(value)
  }, [])

  const handleCreateItinerary = useCallback((days?: number) => {
    const daysToUse = days ?? itineraryDays
    closeCreateItineraryDropdown()
    setIsGeneratingItinerary(true)
    setRequestedDaysCount(daysToUse)
    const displayDaysLabel = intl.formatMessage(
      {
        id: 'WuQGcs5pleJG0thhNUz_',
        defaultMessage: '{count, plural, one {# day} other {# days}}',
        description: 'Day count for itinerary creation prompt on the itinerary or schedule view',
      },
      { count: daysToUse },
    )
    const contextDaysLabel = daysToUse === 1 ? '1 day' : `${daysToUse} days`
    const displayMessage = intl.formatMessage(
      {
        id: 'qW6V5zYOF73N6_jtHmYX',
        defaultMessage: 'Create a full, {daysLabel} itinerary for {placeName}',
        description: 'User chat message shown when creating a full itinerary for a place with a chosen day count',
      },
      {
        daysLabel: displayDaysLabel,
        placeName,
      },
    )
    onSendMessage(
      appendContextTag(
        displayMessage,
        `Create a full, ${contextDaysLabel} itinerary for ${placeName}`,
        TRIP_PLANNER_ACTION_CONTEXT_LABEL,
      ),
    )
  }, [intl, closeCreateItineraryDropdown, itineraryDays, onSendMessage, placeName])

  const handleCreateItineraryWithStopDuration = useCallback(() => {
    if (stopDurationDayCount > 0) {
      handleCreateItinerary(stopDurationDayCount)
    }
  }, [stopDurationDayCount, handleCreateItinerary])

  const handleCreateItineraryFromDropdown = useCallback(() => {
    handleCreateItinerary()
  }, [handleCreateItinerary])

  const handleDelete = useCallback(async() => {
    if (actionsDisabled) return
    const confirmed = await showModal<boolean>(<ConfirmDeleteStopModal placeName={placeName} />)
    if (!confirmed) return
    // Captured once the customer confirms, not after the delete request round-trips below.
    const occurredAt = new Date().toISOString()
    const deleted = await onDeleteTarget?.({ targetType: 'stop', stopId }, placeName)
    if (deleted) {
      eventBus?.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildStopDeletedActivityEntry(placeName, 'trip_planner.itinerary.canvas.delete_stop', occurredAt)],
      })
    }
  }, [actionsDisabled, showModal, placeName, onDeleteTarget, stopId, eventBus])

  const handleOpenHotelDrawer = useCallback(() => {
    setIsHotelDrawerOpen(true)
    onDrawerOpenChange?.(true)
  }, [onDrawerOpenChange])

  const handleHotelActionsClick = useCallback(() => {
    setIsHotelDrawerOpen(true)
    onDrawerOpenChange?.(true)
  }, [onDrawerOpenChange])

  const handleCloseHotelDrawer = useCallback(() => {
    setIsHotelDrawerOpen(false)
    onDrawerOpenChange?.(false)
  }, [onDrawerOpenChange])

  const handleOpenExperienceDrawer = useCallback(() => {
    setIsExperienceDrawerOpen(true)
    onDrawerOpenChange?.(true)
  }, [onDrawerOpenChange])

  const handleCloseExperienceDrawer = useCallback(() => {
    setIsExperienceDrawerOpen(false)
    onDrawerOpenChange?.(false)
  }, [onDrawerOpenChange])

  const handleShowMap = useCallback((hotel: DrawerHotel) => {
    if (!onShowMap) return
    handleCloseHotelDrawer()
    const mapViewData: HotelMapViewData = {
      searchQuery: placeName,
      offers: [{
        id: hotel.id,
        name: hotel.name,
        coordinates: hotel.geoLocation!,
      }],
    }
    onShowMap(mapViewData)
  }, [handleCloseHotelDrawer, placeName, onShowMap])

  const handleExploreMap = useCallback(() => {
    if (!onExploreMap) return
    handleCloseHotelDrawer()
    if (stopCoordinates) {
      onExploreMap(stopCoordinates, 'le-hotels')
    }
  }, [handleCloseHotelDrawer, onExploreMap, stopCoordinates])

  const handleExploreExperienceMap = useCallback(() => {
    if (!onExploreMap) return
    handleCloseExperienceDrawer()
    if (stopCoordinates) {
      onExploreMap(stopCoordinates, 'le-experiences')
    }
  }, [handleCloseExperienceDrawer, onExploreMap, stopCoordinates])

  const handleShowExperienceOnMap = useCallback((experience: { offerId: string; name: string; geoLocation: { lat: number; lng: number } }) => {
    handleCloseExperienceDrawer()
    onShowExperienceOnMap?.({
      searchQuery: experience.name,
      offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
    })
  }, [handleCloseExperienceDrawer, onShowExperienceOnMap])

  const handleHighlightClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { googlePlaceId: highlightGooglePlaceId, name, itemId } = event.currentTarget.dataset
    if (highlightGooglePlaceId && name && itemId) {
      setSelectedHighlight({ googlePlaceId: highlightGooglePlaceId, name, itemId: Number(itemId) })
      onDrawerOpenChange?.(true)
    }
  }, [onDrawerOpenChange])

  const handleCloseHighlightDrawer = useCallback(() => {
    setSelectedHighlight(undefined)
    onDrawerOpenChange?.(false)
  }, [onDrawerOpenChange])

  const handleCloseHotelHighlightDrawer = useCallback(() => {
    setSelectedHotelHighlight(undefined)
    onDrawerOpenChange?.(false)
  }, [onDrawerOpenChange])

  const handleDeleteHighlightItem = useCallback(async(itemId: number) => {
    if (actionsDisabled) return
    const highlight = highlights?.find((h) => h.id === itemId)
    if (!highlight) return
    // Closes immediately, same timing as before this handler needed to await anything -- only the
    // activity dispatch below is gated on confirmed success; leaving the drawer open through the
    // whole delete round trip would keep rendering the highlight the customer just asked to remove.
    setSelectedHighlight(undefined)
    onDrawerOpenChange?.(false)
    // Captured before the delete request round-trips below, not after.
    const occurredAt = new Date().toISOString()
    const deleted = await onDeleteTarget?.({ targetType: 'item', itemId }, highlight.name)
    if (deleted) {
      eventBus?.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildItemDeletedActivityEntry(highlight.name, 'trip_planner.itinerary.canvas.delete_highlight', occurredAt)],
      })
    }
  }, [actionsDisabled, highlights, onDeleteTarget, onDrawerOpenChange, eventBus])

  const handleShowExperienceHighlightOnMap = useCallback((experience: DrawerExperience) => {
    handleCloseHotelHighlightDrawer()
    if (!experience.geoLocation) return
    onShowExperienceOnMap?.({
      searchQuery: experience.name,
      offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
    })
  }, [handleCloseHotelHighlightDrawer, onShowExperienceOnMap])

  const { sortedHighlights } = useSortedHighlights(highlights)
  const hotelMapHandler = onShowMap ? handleShowMap : undefined
  const hotelExploreMapHandler = onExploreMap && stopCoordinates ? handleExploreMap : undefined
  const experienceMapHandler = onShowExperienceOnMap ? handleShowExperienceOnMap : undefined
  const experienceHighlightMapHandler = onShowExperienceOnMap ? handleShowExperienceHighlightOnMap : undefined
  const experienceExploreMapHandler = onExploreMap && stopCoordinates ? handleExploreExperienceMap : undefined

  const hotelHighlightHandlers = useMemo(() => {
    const handlers: Record<number, () => void> = {}

    for (const highlight of sortedHighlights) {
      if (!highlight.googlePlaceId && highlight.savedItemData) {
        const savedData = highlight.savedItemData

        if (isHotelSavedData(savedData)) {
          const searchParams: OfferClickSearchParams = {}
          if (savedData.checkIn) searchParams.checkIn = savedData.checkIn
          if (savedData.checkOut) searchParams.checkOut = savedData.checkOut

          handlers[highlight.id] = () => {
            setSelectedHotelHighlight({
              offerId: savedData.offerId,
              accommodationPropertyId: isSppSavedData(savedData) ? savedData.accommodationPropertyId : undefined,
              offerType: savedData.offerType,
              searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
            })
            onDrawerOpenChange?.(true)
          }
        } else if (isExperienceSavedData(savedData)) {
          handlers[highlight.id] = () => {
            setSelectedHotelHighlight({
              offerId: savedData.experienceId,
              offerType: 'experience',
            })
            onDrawerOpenChange?.(true)
          }
        }
      }
    }

    return handlers
  }, [onDrawerOpenChange, sortedHighlights])

  useEffect(() => {
    if (drawerCloseSignal === previousDrawerCloseSignalRef.current) return

    previousDrawerCloseSignalRef.current = drawerCloseSignal
    setIsHotelDrawerOpen(false)
    setIsExperienceDrawerOpen(false)
    setSelectedHighlight(undefined)
    setSelectedHotelHighlight(undefined)
  }, [drawerCloseSignal])

  const placeNameHeading = googlePlaceId ? (
    <ClickableHeading onClick={onLearnMoreClickHandler}>
      <Heading variant="heading3">{placeName}</Heading>
    </ClickableHeading>
  ) : (
    <Heading variant="heading3">{placeName}</Heading>
  )

  const learnMoreLabel = intl.formatMessage({
    id: '54YyE2EorGEGt1ZOGM1l',
    defaultMessage: 'Learn more',
    description: 'Button label to learn more about a trip destination',
  })

  const addDaysOrDatesLabel = intl.formatMessage({
    id: 'tCy9PycI1Guoepw6mk8q',
    defaultMessage: 'Add days or dates',
    description: 'Action text shown on a trip planner stop with no dates or day count',
  })
  const stopSubtitle = currentStop ? getStopTimingSubtitle(intl, currentStop) : undefined
  const stopSubtitleLabel = stopSubtitle || (currentStop ? addDaysOrDatesLabel : '')
  const stopSubtitleContent = stopSubtitleLabel && currentStop ? (
    <StopTimingEditControl
      stop={currentStop}
      tripStops={data?.stops}
      onSendMessage={onSendMessage}
      disabled={actionsDisabled}
      variant={stopTimingEditVariant}
    >
      {stopSubtitleLabel}
    </StopTimingEditControl>
  ) : stopSubtitleLabel

  const itineraryHeadingLabel = intl.formatMessage({
    id: 'RUICEpJ3xQuhVvPQHvCG',
    defaultMessage: 'Itinerary',
    description: 'Section heading for the trip stop itinerary in the trip planner',
  })

  const highlightsHeadingLabel = intl.formatMessage({
    id: 'vCnI0fSFo0TfSafwS2AE',
    defaultMessage: 'Highlights',
    description: 'Section heading for the trip stop highlights in the trip planner',
  })

  const createItineraryLabel = intl.formatMessage({
    id: 'FKx4t_HttTBgdfFBFUcB',
    defaultMessage: 'Create itinerary',
    description: 'Button label to create an itinerary for a trip stop',
  })

  const createLabel = intl.formatMessage({
    id: '4XOf32wU7_mbMlGZiToJ',
    defaultMessage: 'Create',
    description: 'Primary action label in the create itinerary dropdown',
  })

  const showHighlightFallback = !hasDays && hasHighlights
  const sectionHeadingLabel = showHighlightFallback ? highlightsHeadingLabel : itineraryHeadingLabel

  return (
    <>
      <StopSheet>
        <StopSheetContent direction="vertical" gap={0}>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <StopHeaderChevronButton onClick={toggleStopExpanded}>
              <StopHeaderChevron $expanded={isStopExpanded}>
                <LineAngleDownIcon />
              </StopHeaderChevron>
            </StopHeaderChevronButton>
            {currentStop?.isPlaceLookupApplicable === false && (
              <ImageContainer as="div" aria-hidden="true">
                <Group direction="horizontal" horizontalAlign="center" verticalAlign="center" fullWidth fullHeight>
                  <LineMapMarkerIcon size="XL" />
                </Group>
              </ImageContainer>
            )}
            {currentStop?.isPlaceLookupApplicable !== false && (!!imageId || isEnriching) && (
              <ImageContainer onClick={googlePlaceId ? onLearnMoreClickHandler : undefined}>
                {imageId && <ResponsiveImage aspectRatio="1:1" id={imageId} fit="center" alt={placeName} />}
                {!imageId && <PlaceImageLoader itemId={stopId} />}
              </ImageContainer>
            )}
            <StopMainContent direction="vertical" gap={2} verticalAlign="center" fullWidth>
              <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="start" fullWidth>
                <StopTitleContent direction="vertical" gap={2}>
                  {placeNameHeading}
                  {stopSubtitleContent && <BodyText variant="small" colour="neutral-two">{stopSubtitleContent}</BodyText>}
                </StopTitleContent>
                <Group direction="horizontal" gap={8} verticalAlign="start">
                  <ChatItemDropdown onDelete={handleDelete} />
                </Group>
              </Group>
              {googlePlaceId && (
                <StopLearnMoreButton size="small" kind="tertiary" onClick={onLearnMoreClickHandler}>
                  {learnMoreLabel}
                </StopLearnMoreButton>
              )}
            </StopMainContent>
          </Group>
          <StopDetailsPanel $expanded={isStopExpanded}>
            <StopDetailsContent $expanded={isStopExpanded}>
              {currentStop && (
                <StopDestinationRefinement stop={currentStop} onSendMessage={onSendMessage} disabled={actionsDisabled} />
              )}
              {showProductTiles && (
                <Group direction="vertical" gap={12}>
                  <TripPlanHotelTile
                    bookedHotelSummary={bookedHotelSummary}
                    hasSavedHotels={hasSavedHotels}
                    hotelTilePrice={hotelTilePrice}
                    onActionsClick={handleHotelActionsClick}
                    onOpen={handleOpenHotelDrawer}
                    savedHotelCount={savedHotelCount}
                  />
                  <TripPlanExperienceTile
                    bookedExperienceSummary={bookedExperienceSummary}
                    bookedExperienceCount={bookedExperienceCount}
                    hasSavedExperiences={hasSavedExperiences}
                    experienceTilePrice={experienceTilePrice}
                    onActionsClick={handleOpenExperienceDrawer}
                    onOpen={handleOpenExperienceDrawer}
                    savedExperienceCount={savedExperienceCount}
                  />
                </Group>
              )}
              {(hasHighlights || hasDays || hasItemsWithoutDay) && (
                <StopItinerarySheet>
                  <Group direction="vertical" gap={8}>
                    {!hasDays && !isGeneratingItinerary && (
                      <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between">
                        <BodyText variant="large" weight="bold">{sectionHeadingLabel}</BodyText>
                        <>
                          <TextButton
                            size="medium"
                            kind="primary"
                            shape="round"
                            startIcon={<SolidSparklesIcon />}
                            onClick={stopDurationDayCount > 0 ? handleCreateItineraryWithStopDuration : toggleCreateItineraryDropdown}
                            ref={createItineraryTriggerRef}
                            disabled={isStreaming}
                          >
                            {createItineraryLabel}
                          </TextButton>
                          {!(stopDurationDayCount > 0) && (
                            <ResponsiveDropdownList
                              size="S"
                              open={isCreateItineraryOpen}
                              onClose={closeCreateItineraryDropdown}
                              triggerRef={createItineraryTriggerRef}
                              anchorRef={createItineraryTriggerRef}
                              placement="bottom-end"
                              portaled
                              primaryActionProps={{ children: createLabel, onClick: handleCreateItineraryFromDropdown }}
                              footerActionsLayout="stacked"
                              enableFlip
                            >
                              <Sheet padding={16}>
                                <Group direction="vertical" gap={8} horizontalAlign="start">
                                  <BodyText variant="medium">
                                    {intl.formatMessage(
                                      {
                                        id: 'CsC8uRppEP6LyE7SIfDU',
                                        defaultMessage: 'How many days would you like to spend in <strong>{placeName}</strong>?',
                                        description: 'Body text asking user to choose number of days for creating an itinerary',
                                      },
                                      {
                                        placeName: formatPlacePhrase({ location: { name: placeName }, language: intl.locale, intent: 'location' }),
                                        strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                                      },
                                    )}
                                  </BodyText>
                                  <Counter
                                    value={itineraryDays}
                                    min={1}
                                    max={365}
                                    onChange={onItineraryDaysChange}
                                    size="large"
                                  />
                                </Group>
                              </Sheet>
                            </ResponsiveDropdownList>
                          )}
                        </>
                      </Group>
                    )}
                    {showHighlightFallback && !isGeneratingItinerary && (
                      <Group direction="vertical" gap={8}>
                        {sortedHighlights.map((highlight) => (
                          <Group key={`${highlight.id}-${highlight.name}`} direction="horizontal" gap={8} verticalAlign="start">
                            <BodyText variant="medium" as="span">
                              {highlight.emoji}
                            </BodyText>
                            <BodyText variant="medium">
                              {highlight.googlePlaceId && (
                                <ClickableHighlightTitle
                                  data-google-place-id={highlight.googlePlaceId}
                                  data-name={highlight.name}
                                  data-item-id={highlight.id}
                                  onClick={handleHighlightClick}
                                >
                                  {highlight.name}
                                </ClickableHighlightTitle>
                              )}
                              {!highlight.googlePlaceId && hotelHighlightHandlers[highlight.id] && (
                                <ClickableHighlightTitle onClick={hotelHighlightHandlers[highlight.id]}>
                                  {highlight.name}
                                </ClickableHighlightTitle>
                              )}
                              {!highlight.googlePlaceId && !hotelHighlightHandlers[highlight.id] && <b>{highlight.name}</b>}
                              {formatDashSeparator(intl)}{highlight.description}
                            </BodyText>
                          </Group>
                        ))}
                      </Group>
                    )}
                    {isGeneratingItinerary && !hasDays && (
                      <ItineraryLoadingState daysCount={requestedDaysCount} />
                    )}
                    {(hasDays || (hasItemsWithoutDay && !showHighlightFallback)) && (
                      <DayItineraryCanvas
                        data={data}
                        stopId={stopId}
                        onSendMessage={onSendMessage}
                        onDeleteTarget={onDeleteTarget}
                        onShowMap={hotelMapHandler}
                        onShowExperienceOnMap={onShowExperienceOnMap}
                        onAskAI={onAskAI}
                        onAskAIForItem={onAskAIForItem}
                        scrollTargetDayId={scrollTargetDayId}
                        scrollTargetItemId={scrollTargetItemId}
                      />
                    )}
                  </Group>
                </StopItinerarySheet>
              )}
            </StopDetailsContent>
          </StopDetailsPanel>
        </StopSheetContent>
      </StopSheet>
      {selectedHighlight && (
        <PlaceInfoDrawer
          isOpen
          onClose={handleCloseHighlightDrawer}
          googlePlaceId={selectedHighlight.googlePlaceId}
          placeName={selectedHighlight.name}
          itemId={selectedHighlight.itemId}
          onDelete={handleDeleteHighlightItem}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}
      <HotelListingDrawer
        isOpen={isHotelDrawerOpen}
        onClose={handleCloseHotelDrawer}
        placeName={placeName}
        placeCountry={placeCountry}
        stopStartDate={currentStop?.startDate}
        stopEndDate={currentStop?.endDate}
        stopCoordinates={stopCoordinates}
        contextStop={currentStop}
        onShowMap={hotelMapHandler}
        onExploreMap={hotelExploreMapHandler}
        itineraryData={data}
        onSendMessage={onSendMessage}
        initialSelectedOfferId={bookedHotelSummary?.offerId}
        initialSelectedOfferType={bookedHotelSummary?.offerType}
      />
      <ExperienceListingDrawer
        isOpen={isExperienceDrawerOpen}
        onClose={handleCloseExperienceDrawer}
        placeName={placeName}
        placeCountry={placeCountry}
        contextStop={currentStop}
        stopCoordinates={stopCoordinates}
        onExploreMap={experienceExploreMapHandler}
        itineraryData={data}
        onSendMessage={onSendMessage}
        onShowExperienceOnMap={experienceMapHandler}
        initialSelectedOfferId={bookedExperienceSummary?.offerId}
      />
      {selectedHotelHighlight?.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          offerId={selectedHotelHighlight.offerId}
          isOpen
          onClose={handleCloseHotelHighlightDrawer}
          onShowMap={experienceHighlightMapHandler}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}
      {selectedHotelHighlight && selectedHotelHighlight.offerType !== 'experience' && (
        <ChatOfferDetailDrawer
          offerId={selectedHotelHighlight.offerId}
          accommodationPropertyId={selectedHotelHighlight.accommodationPropertyId}
          offerType={selectedHotelHighlight.offerType}
          isOpen
          onClose={handleCloseHotelHighlightDrawer}
          onShowMap={hotelMapHandler}
          itineraryData={data}
          onSendMessage={onSendMessage}
          searchParams={selectedHotelHighlight.searchParams}
        />
      )}
    </>
  )
}

export default TripPlanCanvasItem
